import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Plus } from 'lucide-react';
import { WindFarm } from '../types';
import WindFarmModal from '../components/WindFarmModal';
import { saveProjectMetadata, getProjectMetadata, generateProjectId } from '../utils/r2Storage';
import 'leaflet/dist/leaflet.css';

const PageContainer = styled.div`
  padding: 30px;
  max-width: 1600px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  
  div {
    h1 {
      font-size: 32px;
      font-weight: 700;
      color: #1a1f3a;
      margin: 0 0 8px 0;
    }
    
    p {
      color: #6b7280;
      margin: 0;
    }
  }
`;

const AddButton = styled.button`
  background: linear-gradient(135deg, #00d4ff 0%, #0099ff 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 153, 255, 0.3);
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const Section = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 30px;
  
  h2 {
    font-size: 16px;
    font-weight: 600;
    color: #6b7280;
    margin: 0 0 16px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th {
    text-align: left;
    padding: 12px;
    background: #f9fafb;
    color: #6b7280;
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid #e5e7eb;
  }
  
  td {
    padding: 12px;
    border-top: 1px solid #f3f4f6;
    color: #1a1f3a;
    font-size: 14px;
  }
  
  tr {
    cursor: pointer;
    transition: background 0.2s ease;
    
    &:hover {
      background: #f9fafb;
    }
  }
`;

const MapContainer2 = styled.div`
  height: 450px;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 16px;
  
  .leaflet-container {
    height: 100%;
    border-radius: 8px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
  
  p {
    margin: 16px 0;
    font-size: 16px;
  }
`;

const WindFarms: React.FC = () => {
  const navigate = useNavigate();
  const [farms, setFarms] = useState<WindFarm[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    loadFarms();
  }, []);

  const loadFarms = async () => {
    try {
      const metadata: any = await getProjectMetadata('farms-list');
      if (metadata && metadata.farms) {
        setFarms(metadata.farms);
      } else {
        setFarms([]);
      }
    } catch (error) {
      console.error('Failed to load farms:', error);
      setFarms([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddFarm = () => {
    setIsModalOpen(true);
  };

  const handleSaveFarm = async (farmData: Omit<WindFarm, 'id' | 'createdAt'>) => {
    try {
      const newFarm: WindFarm = {
        ...farmData,
        id: generateProjectId('farm'),
        createdAt: new Date().toISOString(),
      };

      const updatedFarms = [...farms, newFarm];
      setFarms(updatedFarms);

      // Save to R2
      await saveProjectMetadata('farms-list', { farms: updatedFarms } as any);
    } catch (error) {
      console.error('Failed to save farm:', error);
      alert('Santral kaydedilemedi. Lütfen tekrar deneyin.');
    }
  };

  const handleFarmClick = (farmId: string) => {
    // Navigate to general turbines list instead of farm-specific
    navigate(`/turbines`);
  };

  const defaultCenter: [number, number] = [39.0, 35.0];

  return (
    <PageContainer>
      <Header>
        <div>
          <h1>WIND FARMS</h1>
          <p>List of all your wind farms</p>
        </div>
        <AddButton onClick={handleAddFarm}>
          <Plus />
          Add Wind Farm
        </AddButton>
      </Header>

      {farms.length > 0 ? (
        <>
          <Section>
            <h2>Wind Farm List</h2>
            <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>
              You can access turbine information by clicking on the wind farm name.
            </p>
            <Table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Wind Farm Name</th>
                  <th>City</th>
                  <th>Turbine Quantity</th>
                  <th>Installed Power</th>
                </tr>
              </thead>
              <tbody>
                {farms.map((farm, index) => (
                  <tr key={farm.id} onClick={() => handleFarmClick(farm.id)}>
                    <td>{index + 1}</td>
                    <td>{farm.name}</td>
                    <td>{farm.city}</td>
                    <td>{farm.turbineCount}</td>
                    <td>{farm.installedPower} MW</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Section>

          <Section>
            <h2>Turbine Map</h2>
            <p style={{ color: '#6b7280', fontSize: '14px' }}>
              You can access the turbine name by clicking on the turbine icon.
            </p>
            <MapContainer2>
              <MapContainer
                center={defaultCenter}
                zoom={6}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {farms.map((farm) => (
                  <Marker key={farm.id} position={[farm.location.lat, farm.location.lng]}>
                    <Popup>
                      <strong>{farm.name}</strong><br />
                      {farm.city}, {farm.country}<br />
                      {farm.turbineCount} turbines
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </MapContainer2>
          </Section>
        </>
      ) : (
        <Section>
          <EmptyState>
            <h2>No wind farms yet</h2>
            <p>Get started by adding your first wind farm</p>
            <AddButton onClick={handleAddFarm} style={{ margin: '20px auto 0' }}>
              <Plus />
              Add Wind Farm
            </AddButton>
          </EmptyState>
        </Section>
      )}

      <WindFarmModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveFarm}
      />
    </PageContainer>
  );
};

export default WindFarms;
